<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { MdPreview } from 'md-editor-v3'
import 'md-editor-v3/lib/style.css'
import DOMPurify from 'dompurify'

const props = withDefaults(
  defineProps<{
    /** 마크다운 또는 HTML 내용 */
    modelValue?: string
    /** 테마 (light/dark) - 설정하지 않으면 자동으로 다크모드 감지 */
    theme?: 'light' | 'dark'
    /** 추가 CSS 클래스 */
    class?: string
  }>(),
  {
    modelValue: '',
  },
)

// 다크모드 상태
const isDarkMode = ref(false)

// 다크모드 감지 함수
const detectDarkMode = () => {
  isDarkMode.value = document.documentElement.classList.contains('dark')
}

// 현재 테마 계산 (props.theme이 있으면 그것 사용, 없으면 자동 감지)
const currentTheme = computed(() => {
  if (props.theme) {
    return props.theme
  }
  return isDarkMode.value ? 'dark' : 'light'
})

// MutationObserver로 다크모드 변경 감지
let darkModeObserver: MutationObserver | null = null

onMounted(() => {
  // 초기 다크모드 상태 감지
  detectDarkMode()
  
  // MutationObserver로 class 변경 감지
  darkModeObserver = new MutationObserver(() => {
    detectDarkMode()
  })
  
  darkModeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  })
})

onUnmounted(() => {
  if (darkModeObserver) {
    darkModeObserver.disconnect()
  }
})

/**
 * 콘텐츠 타입 자동 감지
 */
function detectContentType(content: string): 'markdown' | 'html' {
  const trimmed = content.trim()
  
  // HTML 문서 시작 태그 확인
  if (
    trimmed.startsWith('<!DOCTYPE html') ||
    trimmed.startsWith('<!doctype html') ||
    trimmed.startsWith('<!DOCTYPE HTML') ||
    trimmed.startsWith('<!DOCTYPE Html') ||
    trimmed.startsWith('<html') ||
    trimmed.startsWith('<HTML') ||
    trimmed.startsWith('<Html')
  ) {
    return 'html'
  }
  
  return 'markdown'
}

const contentType = computed(() => detectContentType(props.modelValue || ''))

const sanitizedHtml = computed(() => {
  if (contentType.value === 'html') {
    return DOMPurify.sanitize(props.modelValue || '', {
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li', 'blockquote', 'pre', 'code', 'a', 'img', 'table', 'thead',
        'tbody', 'tr', 'th', 'td', 'div', 'span', 'hr', 'blockquote', 'del', 'ins',
        'sub', 'sup', 'kbd', 'mark', 'abbr', 'dfn', 'cite', 'q', 'samp', 'var',
        'time', 'b', 'i', 'small', 'big', 'center', 'font', 'strike', 'tt',
        'html', 'head', 'body', 'title', 'meta', 'link', 'style', 'script', 'noscript'
      ],
      ALLOWED_ATTR: [
        'href', 'src', 'alt', 'title', 'width', 'height', 'class', 'id', 'style',
        'target', 'rel', 'type', 'charset', 'name', 'content', 'http-equiv', 'lang'
      ],
      ALLOW_DATA_ATTR: false,
    })
  }
  return ''
})
</script>

<template>
  <div :class="['j-preview-wrapper', props.class]">
    <!-- HTML 렌더링 -->
    <div
      v-if="contentType === 'html'"
      :class="['j-preview-html', currentTheme === 'dark' ? 'dark' : '']"
      v-html="sanitizedHtml"
    />
    
    <!-- 마크다운 렌더링 -->
    <MdPreview
      v-else
      :model-value="modelValue"
      :theme="currentTheme"
      :preview-class-name="'j-preview-markdown'"
      language="en-US"
    />
  </div>
</template>

<style scoped>
.j-preview-wrapper {
  @apply w-full;
}

.j-preview-html {
  @apply prose prose-slate max-w-none;
}

.j-preview-html.dark {
  @apply prose-invert;
}

:deep(.j-preview-markdown) {
  @apply w-full;
}
</style>
